/**
 * The twelve-unit path.
 *
 * Original content, cause first. Each unit answers "why does this happen" before it
 * answers "what is this called", and every unit ends in writing, hearing and using
 * the idea rather than reading about it.
 */

import type { Lesson } from './types';

export const LESSONS: Lesson[] = [
  {
    id: 'sound',
    sequence: 1,
    title: 'Sound, pitch, and the twelve notes',
    idea: 'Pitch is frequency, and doubling a frequency sounds like the same note again.',
    cause:
      'Fast vibration is heard as high, slow as low. Double the speed of a vibration and the ear does not hear a new note, it hears the same note higher up — that is why the letters run out at seven and start again. Between one C and the next there are twelve equal steps, and every note you will ever write is one of those twelve.',
    demonstration: {
      caption: 'The same letter, an octave apart. Two frequencies, one identity.',
      clef: 'treble',
      groups: [['C4'], ['C5'], ['C4', 'C5']],
      labels: ['C', 'C an octave up', 'both at once'],
    },
    guidedInkTask: {
      prompt: 'Write middle C, then the C an octave above it.',
      clef: 'treble',
      expect: ['C4', 'C5'],
      ignoreOctave: false,
      hint: 'Middle C sits on a ledger line below the treble staff. The C above is in the third space.',
    },
    earTask: {
      prompt: 'Are these two notes the same letter, or different?',
      kind: 'interval',
      choices: ['Octave', 'Perfect 5th', 'Major 3rd'],
    },
    creativeTask: {
      prompt: 'Write any five notes you like. Do not worry about whether it is good.',
      clef: 'treble',
      lookFor: 'Getting comfortable putting marks on the staff without judging them.',
    },
    masteryRule: {
      earStreak: 3,
      requireInk: true,
      requireCreative: false,
      description: 'Write both Cs, and hear an octave three times running.',
    },
    prerequisites: [],
  },
  {
    id: 'staff',
    sequence: 2,
    title: 'Staff geography',
    idea: 'The staff is a map, and the clef tells you where you are on it.',
    /**
     * Scope note, shown to the learner.
     *
     * The master brief describes this unit as staff geography *and rhythmic
     * value*. Rhythm notation is not implemented — there are no beams, flags,
     * rests, dotted notes, ties or time signatures, and no duration model to
     * grade them against. Rather than quietly teach half a unit, the unit says
     * what it covers and what it does not.
     */
    scopeNote:
      'This unit covers where notes sit on the staff. Rhythm notation — note lengths, rests and ties — is not part of this pilot yet.',
    cause:
      'Five lines and four spaces cannot show every note, so a clef anchors one line to one pitch and everything else follows by stepping. Change the clef and the same dot on the same line becomes a different note — the shape of the writing did not change, the reference point did.',
    demonstration: {
      caption: 'The same three positions, read in treble and then in bass.',
      clef: 'treble',
      groups: [['E4'], ['G4'], ['B4']],
      labels: ['bottom line', 'second line', 'middle line'],
    },
    guidedInkTask: {
      prompt: 'Write the notes on the bottom line, middle line, and top line of the treble staff.',
      clef: 'treble',
      expect: ['E4', 'B4', 'F5'],
      ignoreOctave: false,
      hint: 'Bottom line E, middle line B, top line F. Centre each notehead on the line itself.',
    },
    earTask: {
      prompt: 'Which note did you hear, relative to the first?',
      kind: 'scaleDegree',
      choices: ['1', '3', '5'],
    },
    creativeTask: {
      prompt: 'Write a short line that only uses notes sitting on lines, never spaces.',
      clef: 'treble',
      lookFor: 'Reading position confidently rather than counting up from the bottom every time.',
    },
    masteryRule: {
      earStreak: 3,
      requireInk: true,
      requireCreative: false,
      description: 'Place notes accurately on lines, and hear scale degrees three times running.',
    },
    prerequisites: ['sound'],
  },
  {
    id: 'steps',
    sequence: 3,
    title: 'Half steps, whole steps, and distance',
    idea: 'Every interval is just a count of half steps, given a name.',
    cause:
      'A half step is the smallest distance in the system — adjacent keys, adjacent frets. Everything larger is built by stacking them. Once you count distance in half steps, interval names stop being vocabulary to memorise and become arithmetic you can check.',
    demonstration: {
      caption: 'C to E is four half steps. C to E flat is three. One semitone changes everything.',
      clef: 'treble',
      groups: [['C4', 'E4'], ['C4', 'Eb4']],
      labels: ['major 3rd — 4 half steps', 'minor 3rd — 3'],
    },
    guidedInkTask: {
      prompt: 'Write C, then the note a major third above it, then the note a minor third above C.',
      clef: 'treble',
      expect: ['C4', 'E4', 'Eb4'],
      ignoreOctave: true,
      hint: 'A major third is four half steps; a minor third is three. Both are written on the letter E.',
    },
    earTask: {
      prompt: 'Major or minor third?',
      kind: 'interval',
      choices: ['Major 3rd', 'Minor 3rd'],
    },
    creativeTask: {
      prompt: 'Write a line where every leap is a third, up or down.',
      clef: 'treble',
      lookFor: 'Hearing thirds as a distance rather than a shape on the page.',
    },
    masteryRule: {
      earStreak: 4,
      requireInk: true,
      requireCreative: false,
      description: 'Write both thirds, and tell them apart by ear four times running.',
    },
    prerequisites: ['staff'],
  },
  {
    id: 'scales',
    sequence: 4,
    title: 'Scale formulas',
    idea: 'A scale is a repeatable pattern of steps, not a list to memorise.',
    cause:
      'The major scale is whole–whole–half–whole–whole–whole–half. Start that pattern anywhere and you get a major scale in that key — the accidentals are not arbitrary decoration, they are whatever is required to keep the pattern intact. That is the entire reason key signatures exist.',
    demonstration: {
      caption: 'The same pattern from C needs nothing; from G it needs one sharp.',
      clef: 'treble',
      groups: [['C4'], ['D4'], ['E4'], ['F4'], ['G4'], ['A4'], ['B4'], ['C5']],
    },
    guidedInkTask: {
      prompt: 'Write the first five notes of a G major scale.',
      clef: 'treble',
      expect: ['G4', 'A4', 'B4', 'C5', 'D5'],
      ignoreOctave: false,
      hint: 'G A B C D — no accidental needed yet. The F sharp only arrives on the seventh note.',
    },
    earTask: {
      prompt: 'Which scale degree was that, over the tonic?',
      kind: 'scaleDegree',
      choices: ['1', '2', '3', '5'],
    },
    creativeTask: {
      prompt: 'Write a melody using only the notes of one major scale.',
      clef: 'treble',
      lookFor: 'Staying inside a key on purpose, and hearing when you leave it.',
    },
    masteryRule: {
      earStreak: 4,
      requireInk: true,
      requireCreative: true,
      description: 'Write the scale, hear degrees four times running, and compose in a key.',
    },
    prerequisites: ['steps'],
  },
  {
    id: 'tonal-centre',
    sequence: 5,
    title: 'Tonal centre',
    idea: 'The same notes point at different homes depending on what surrounds them.',
    cause:
      'A tonal centre is not a property of a note, it is a habit the ear forms from context. Play the same seven notes but keep returning to A instead of C and the ear relocates home. Nothing about the notes changed — the weighting did.',
    demonstration: {
      caption: 'Identical notes. First resolving to C, then resolving to A.',
      clef: 'treble',
      groups: [['C4', 'E4', 'G4'], ['A3', 'C4', 'E4']],
      labels: ['home feels like C', 'home feels like A'],
    },
    guidedInkTask: {
      prompt: 'Write the chord built on A using only white notes: A, C, E.',
      clef: 'treble',
      expect: ['A3', 'C4', 'E4'],
      ignoreOctave: true,
      hint: 'Stack thirds from A without adding accidentals and you get A minor.',
    },
    earTask: {
      prompt: 'Does this resolve to a major or a minor home?',
      kind: 'quality',
      choices: ['Major triad', 'Minor triad'],
    },
    creativeTask: {
      prompt: 'Write a short phrase that ends somewhere other than where it started.',
      clef: 'treble',
      lookFor: 'Noticing that ending elsewhere feels unfinished — that feeling is the tonal centre.',
    },
    masteryRule: {
      earStreak: 4,
      requireInk: true,
      requireCreative: false,
      description: 'Build the chord on A, and hear major against minor four times running.',
    },
    prerequisites: ['scales'],
  },
  {
    id: 'triads',
    sequence: 6,
    title: 'Triads and quality',
    idea: 'Stack two thirds and the order of their sizes decides the quality.',
    cause:
      'Wide third on the bottom, narrow on top: major, and it sounds settled. Reverse them: minor. Two narrow thirds: diminished, whose outer interval is a tritone and refuses to sit still. Two wide thirds: augmented, which divides the octave evenly and therefore has no anchor at all. Four qualities, one rule.',
    demonstration: {
      caption: 'All four triads on C. Only the thirds change.',
      clef: 'treble',
      groups: [
        ['C4', 'E4', 'G4'],
        ['C4', 'Eb4', 'G4'],
        ['C4', 'Eb4', 'Gb4'],
        ['C4', 'E4', 'G#4'],
      ],
      labels: ['C', 'Cm', 'C°', 'C+'],
    },
    guidedInkTask: {
      prompt: 'Write a C minor triad.',
      clef: 'treble',
      expect: ['C4', 'Eb4', 'G4'],
      ignoreOctave: true,
      hint: 'Same C and G as C major; lower only the third, and write the flat to its left.',
    },
    earTask: {
      prompt: 'Which triad quality is this?',
      kind: 'quality',
      choices: ['Major triad', 'Minor triad', 'Diminished triad', 'Augmented triad'],
    },
    creativeTask: {
      prompt: 'Write three triads in a row that sound like they belong together.',
      clef: 'treble',
      lookFor: 'Choosing chords by ear and then being able to explain the choice.',
    },
    masteryRule: {
      earStreak: 4,
      requireInk: true,
      requireCreative: true,
      description: 'Write a minor triad, identify all four qualities four times running, and use them.',
    },
    prerequisites: ['tonal-centre'],
  },
  {
    id: 'sevenths',
    sequence: 7,
    title: 'Sevenths and what they want',
    idea: 'Adding a fourth note turns a colour into a direction.',
    cause:
      'A major seventh sits a semitone below the root and shimmers without moving. A minor seventh over a major triad creates a tritone between the 3rd and the 7th, and that tritone is the engine of nearly every cadence in Western music — it is unstable in a specific direction. Which seventh you add decides whether the chord rests or pushes.',
    demonstration: {
      caption: 'Same triad, two different sevenths, two different intentions.',
      clef: 'treble',
      groups: [
        ['C4', 'E4', 'G4', 'B4'],
        ['C4', 'E4', 'G4', 'Bb4'],
      ],
      labels: ['Cmaj7 — rests', 'C7 — pushes'],
    },
    guidedInkTask: {
      prompt: 'Write a G dominant seventh chord.',
      clef: 'treble',
      expect: ['G4', 'B4', 'D5', 'F5'],
      ignoreOctave: true,
      hint: 'G major triad plus the note a minor seventh above G. The tritone is between B and F.',
    },
    earTask: {
      prompt: 'Which seventh chord is this?',
      kind: 'quality',
      choices: ['Major seventh', 'Dominant seventh', 'Minor seventh', 'Half-diminished seventh'],
    },
    creativeTask: {
      prompt: 'Write a dominant seventh, then the chord you think it wants to move to.',
      clef: 'treble',
      lookFor: 'Letting the tritone resolve — the 3rd rising, the 7th falling.',
    },
    masteryRule: {
      earStreak: 4,
      requireInk: true,
      requireCreative: true,
      description: 'Write a dominant seventh, identify four seventh types, and resolve one.',
    },
    prerequisites: ['triads'],
  },
  {
    id: 'inversions',
    sequence: 8,
    title: 'Inversions and the bass',
    idea: 'The lowest note changes the feel without changing the chord.',
    cause:
      'Move the root up an octave and the notes are identical, but the ear takes its bearings from the bottom. A chord in root position feels planted; the same chord over its third feels like it is passing through. This is why lead sheets bother writing a slash.',
    demonstration: {
      caption: 'One chord, three bass notes.',
      clef: 'treble',
      groups: [
        ['C4', 'E4', 'G4'],
        ['E4', 'G4', 'C5'],
        ['G4', 'C5', 'E5'],
      ],
      labels: ['C', 'C/E', 'C/G'],
    },
    guidedInkTask: {
      prompt: 'Write a C major triad in first inversion — with E in the bass.',
      clef: 'treble',
      expect: ['E4', 'G4', 'C5'],
      ignoreOctave: false,
      hint: 'Same three letters, but E is now the lowest and C sits on top.',
    },
    earTask: {
      prompt: 'Which triad quality is this, whatever its bass?',
      kind: 'quality',
      choices: ['Major triad', 'Minor triad', 'Diminished triad'],
    },
    creativeTask: {
      prompt: 'Write the same chord twice — once rooted, once inverted — and listen to the difference.',
      clef: 'treble',
      lookFor: 'Hearing the bass as the thing that decides stability.',
    },
    masteryRule: {
      earStreak: 3,
      requireInk: true,
      requireCreative: false,
      description: 'Write a first inversion and keep identifying quality regardless of the bass.',
    },
    prerequisites: ['sevenths'],
  },
  {
    id: 'diatonic',
    sequence: 9,
    title: 'The chords a key contains',
    idea: 'Build a triad on every degree of a scale and the qualities are fixed.',
    cause:
      'Take a major scale and stack thirds on each note using only notes from that scale. The pattern of qualities that falls out is always the same: major, minor, minor, major, major, minor, diminished. You do not choose it and you do not memorise it per key — it is a consequence of the scale formula.',
    demonstration: {
      caption: 'Triads on the first four degrees of C major.',
      clef: 'treble',
      groups: [
        ['C4', 'E4', 'G4'],
        ['D4', 'F4', 'A4'],
        ['E4', 'G4', 'B4'],
        ['F4', 'A4', 'C5'],
      ],
      labels: ['I', 'ii', 'iii', 'IV'],
    },
    guidedInkTask: {
      prompt: 'Write the chord built on the second degree of C major.',
      clef: 'treble',
      expect: ['D4', 'F4', 'A4'],
      ignoreOctave: true,
      hint: 'Stack thirds from D using only white notes: D, F, A. It comes out minor.',
    },
    earTask: {
      prompt: 'Major or minor?',
      kind: 'quality',
      choices: ['Major triad', 'Minor triad'],
    },
    creativeTask: {
      prompt: 'Write a four-chord progression using only chords from one key.',
      clef: 'treble',
      lookFor: 'Progressions that stay in key and still go somewhere.',
    },
    masteryRule: {
      earStreak: 4,
      requireInk: true,
      requireCreative: true,
      description: 'Build a diatonic chord, hear qualities, and write a progression in key.',
    },
    prerequisites: ['inversions'],
  },
  {
    id: 'modes',
    sequence: 10,
    title: 'Modes as weighting',
    idea: 'A mode is not a new scale, it is a different note treated as home.',
    cause:
      'The white notes from D to D contain exactly the same pitches as C major. What makes it sound different is that the ear is being pointed at D, so the intervals are now measured from D — and a minor third and a major sixth above the tonic is what Dorian sounds like. Learn the weighting, not seven new shapes.',
    demonstration: {
      caption: 'Identical notes, two different homes.',
      clef: 'treble',
      groups: [['C4', 'E4', 'G4'], ['D4', 'F4', 'A4']],
      labels: ['centred on C', 'centred on D'],
    },
    guidedInkTask: {
      prompt: 'Write the first three notes of D Dorian.',
      clef: 'treble',
      expect: ['D4', 'E4', 'F4'],
      ignoreOctave: false,
      hint: 'White notes starting from D. The third is minor, which is what makes it modal.',
    },
    earTask: {
      prompt: 'Does the third above the home note sound major or minor?',
      kind: 'quality',
      choices: ['Major triad', 'Minor triad'],
    },
    creativeTask: {
      prompt: 'Write a phrase using white notes that keeps returning to D.',
      clef: 'treble',
      lookFor: 'Making a mode audible by insisting on its home rather than by changing notes.',
    },
    masteryRule: {
      earStreak: 3,
      requireInk: true,
      requireCreative: true,
      description: 'Write a modal opening and make the centre audible.',
    },
    prerequisites: ['diatonic'],
  },
  {
    id: 'voice-leading',
    sequence: 11,
    title: 'Voice leading and tension',
    idea: 'Chords connect best when individual notes move as little as possible.',
    cause:
      'Each note in a chord is a voice, and a voice that moves a step is easy to follow while one that leaps is not. Keep common tones where they are and move the rest by the smallest distance available, and progressions stop sounding like a list of chords and start sounding like music.',
    demonstration: {
      caption: 'G7 to C. The tritone resolves outward and inward by semitone.',
      clef: 'treble',
      groups: [
        ['G3', 'B3', 'D4', 'F4'],
        ['C4', 'E4', 'G4'],
      ],
      labels: ['G7', 'C'],
    },
    guidedInkTask: {
      prompt: 'Write the C major triad that G7 resolves to, keeping it close.',
      clef: 'treble',
      expect: ['C4', 'E4', 'G4'],
      ignoreOctave: true,
      hint: 'B rises a semitone to C; F falls a semitone to E. G can stay exactly where it is.',
    },
    earTask: {
      prompt: 'Did that progression sound finished or unfinished?',
      kind: 'cadence',
      choices: ['Finished', 'Unfinished'],
    },
    creativeTask: {
      prompt: 'Write two chords where at least one note stays in the same place.',
      clef: 'treble',
      lookFor: 'Common tones held, and the remaining voices moving by step.',
    },
    masteryRule: {
      earStreak: 4,
      requireInk: true,
      requireCreative: true,
      description: 'Resolve a dominant, hear cadences, and connect chords smoothly.',
    },
    prerequisites: ['modes'],
  },
  {
    id: 'compose',
    sequence: 12,
    title: 'Putting the system to work',
    idea: 'Everything so far exists so you can make choices on purpose.',
    cause:
      'You now have the whole chain: half steps make intervals, intervals make scales, scales make chords, chords make keys, and voice leading connects them. Composition is not a separate skill bolted on at the end — it is what the system was for. Choose a key, choose chords from it, and let the voices move as little as they need to.',
    demonstration: {
      caption: 'A four-chord progression in C, voiced closely.',
      clef: 'treble',
      groups: [
        ['C4', 'E4', 'G4'],
        ['A3', 'C4', 'E4'],
        ['F3', 'A3', 'C4'],
        ['G3', 'B3', 'D4'],
      ],
      labels: ['I', 'vi', 'IV', 'V'],
    },
    guidedInkTask: {
      prompt: 'Write the chord that most strongly wants to return to C.',
      clef: 'treble',
      expect: ['G4', 'B4', 'D5', 'F5'],
      ignoreOctave: true,
      hint: 'The dominant seventh on G. Its tritone points straight at C.',
    },
    earTask: {
      prompt: 'Finished or unfinished?',
      kind: 'cadence',
      choices: ['Finished', 'Unfinished'],
    },
    creativeTask: {
      prompt: 'Write a progression of at least four chords in a key of your choosing, and play it back.',
      clef: 'treble',
      lookFor: 'Deliberate choices you can justify — key, quality, bass, and voice motion.',
    },
    masteryRule: {
      earStreak: 5,
      requireInk: true,
      requireCreative: true,
      description: 'Write the dominant, hear cadences five times running, and compose something you can explain.',
    },
    prerequisites: ['voice-leading'],
  },
];

export const LESSON_BY_ID: Record<string, Lesson> = Object.fromEntries(
  LESSONS.map((l) => [l.id, l]),
);
